import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { messagingService } from '../services/messagingService';
import { patientService } from '../services/patientService';

const emptyForm = {
  subject: '',
  body: '',
  patientId: '',
  toExternalEmail: '',
  channel: 'internal',
};

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [msgRes, patientRes] = await Promise.all([
        messagingService.list(),
        patientService.getPatients({}),
      ]);
      setMessages(msgRes.data || []);
      setPatients(Array.isArray(patientRes) ? patientRes : patientRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.body) {
      alert('Subject and body are required');
      return;
    }
    try {
      setSaving(true);
      await messagingService.send({
        ...form,
        patientId: form.patientId || undefined,
        toExternalEmail: form.toExternalEmail || undefined,
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to send');
    } finally {
      setSaving(false);
    }
  };

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || '—';

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-poppins font-semibold text-primary">Secure Inbox</h1>
              <p className="text-gray-500 mt-1">
                Internal clinical messages linked to patients. Portal delivery comes next.
              </p>
            </div>
            <button
              className="bg-primary text-white px-4 py-2 rounded-lg"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? 'Cancel' : 'Compose'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSend} className="bg-white rounded-xl shadow p-6 mb-6 grid grid-cols-1 gap-4">
              <label className="flex flex-col gap-1 text-sm">
                Subject *
                <input name="subject" value={form.subject} onChange={handleChange} className="border rounded px-3 py-2" required />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Related patient
                <select name="patientId" value={form.patientId} onChange={handleChange} className="border rounded px-3 py-2">
                  <option value="">— Optional —</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                External email (draft / portal invite recipient)
                <input name="toExternalEmail" type="email" value={form.toExternalEmail} onChange={handleChange} className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Body *
                <textarea name="body" value={form.body} onChange={handleChange} rows={4} className="border rounded px-3 py-2" required />
              </label>
              <button type="submit" disabled={saving} className="bg-primary text-white px-5 py-2 rounded-lg w-fit">
                {saving ? 'Sending…' : 'Send'}
              </button>
            </form>
          )}

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && !error && (
            <div className="bg-white rounded-xl shadow divide-y">
              {messages.length === 0 && (
                <div className="p-8 text-center text-gray-500">No messages yet.</div>
              )}
              {messages.map((m) => (
                <div
                  key={m._id}
                  className={`p-4 ${m.isRead ? '' : 'bg-blue-50/40'}`}
                  onClick={async () => {
                    if (!m.isRead) {
                      await messagingService.markRead(m._id);
                      await load();
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="flex justify-between gap-4">
                    <div className="font-medium">{m.subject}</div>
                    <div className="text-xs text-gray-500">
                      {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{m.body}</div>
                  <div className="text-xs text-gray-400 mt-2">
                    {m.patientId
                      ? `Patient: ${patientLabel(typeof m.patientId === 'object' ? m.patientId : null)}`
                      : 'No linked patient'}
                    {m.toExternalEmail ? ` · To: ${m.toExternalEmail}` : ''}
                    {m.isAiDraft ? ' · AI draft' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Messages;
